import { z } from 'zod';
import { CUTOFF_MODES, PAYROLL_PERIOD_KINDS } from './enums';
import { DEFAULT_PRICING_CONFIG, type PricingConfig } from './domain/pricing';

/** Business settings, stored as one row (JSON sections) and edited in Admin > Paramètres. */
export const companySettingsSchema = z.object({
  name: z.string().min(1).max(120),
  phone: z.string().max(40).default(''),
  email: z.string().email().or(z.literal('')).default(''),
  address: z.string().max(240).default(''),
  timezone: z.string().min(1).default('America/Toronto'),
  currency: z.string().length(3).default('CAD'),
  defaultLanguage: z.enum(['fr', 'en']).default('en'),
});

export const planningSettingsSchema = z.object({
  openingTime: z.string().regex(/^\d{2}:\d{2}$/).default('08:00'),
  closingTime: z.string().regex(/^\d{2}:\d{2}$/).default('18:00'),
  workingDays: z.array(z.number().int().min(0).max(6)).default([1, 2, 3, 4, 5, 6]),
  bufferMinutes: z.number().int().min(0).max(180).default(15),
  maxJobsPerEmployeePerDay: z.number().int().min(1).max(20).default(4),
  maxJobsPerTeamPerDay: z.number().int().min(1).max(30).default(5),
  maxHoursPerDay: z.number().min(1).max(16).default(8),
  maxDistanceKm: z.number().min(1).max(300).default(40),
  cutoffMode: z.enum(CUTOFF_MODES).default('HOURS'),
  cutoffHours: z.number().int().min(0).max(240).default(24),
  cutoffBusinessDays: z.number().int().min(0).max(10).default(1),
  /** How many days ahead recurring agreements are materialised into jobs. */
  recurrenceHorizonDays: z.number().int().min(7).max(180).default(60),
});

export const payrollSettingsSchema = z.object({
  periodKind: z.enum(PAYROLL_PERIOD_KINDS).default('BIWEEKLY'),
  /** Anchor date of the first period (local date). */
  periodAnchorDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).default('2026-01-05'),
  defaultMethod: z.enum(['FIXED_PER_JOB', 'HOURLY', 'PERCENTAGE', 'CUSTOM']).default('HOURLY'),
  defaultHourlyRateCents: z.number().int().min(0).default(2200),
  defaultFixedCents: z.number().int().min(0).default(4500),
  defaultPercentage: z.number().min(0).max(1).default(0.5),
});

export const notificationSettingsSchema = z.object({
  reminderEnabled: z.boolean().default(true),
  reminderHoursBefore: z.number().min(0.5).max(72).default(3),
  emailEnabled: z.boolean().default(true),
  employeeDailySummary: z.boolean().default(false),
});

export const pricingExtraSchema = z.object({
  code: z.string().min(1).max(40),
  labelFr: z.string().min(1),
  labelEn: z.string().min(1),
  priceCents: z.number().int(),
  minutes: z.number().int().min(0),
});

export const pricingConfigSchema: z.ZodType<PricingConfig> = z.object({
  basePriceByPropertyType: z.object({
    HOUSE: z.number().int().min(0),
    CONDO: z.number().int().min(0),
    APARTMENT: z.number().int().min(0),
    COMMERCIAL: z.number().int().min(0),
    OTHER: z.number().int().min(0),
  }),
  perBedroomCents: z.number().int().min(0),
  perBathroomCents: z.number().int().min(0),
  perKitchenCents: z.number().int().min(0),
  perFamilyRoomCents: z.number().int().min(0),
  perExtraFloorCents: z.number().int().min(0),
  sqftThreshold: z.number().int().min(0),
  perSqftAboveCents: z.number().min(0),
  petSurchargeCents: z.number().int().min(0),
  frequencyDiscounts: z.object({
    ONE_TIME: z.number().min(0).max(1),
    WEEKLY: z.number().min(0).max(1),
    BIWEEKLY: z.number().min(0).max(1),
    MONTHLY: z.number().min(0).max(1),
    CUSTOM: z.number().min(0).max(1),
  }),
  extras: z.array(pricingExtraSchema),
  minutes: z.object({
    base: z.number().int().min(0),
    perBedroom: z.number().int().min(0),
    perBathroom: z.number().int().min(0),
    perKitchen: z.number().int().min(0),
    perFamilyRoom: z.number().int().min(0),
    perExtraFloor: z.number().int().min(0),
    pets: z.number().int().min(0),
  }),
  minimumPriceCents: z.number().int().min(0),
  roundToCents: z.number().int().min(1),
  secondWorkerAboveMinutes: z.number().int().min(0),
});

export const businessSettingsSchema = z.object({
  company: companySettingsSchema,
  planning: planningSettingsSchema,
  pricing: pricingConfigSchema,
  payroll: payrollSettingsSchema,
  notifications: notificationSettingsSchema,
});

export type CompanySettings = z.infer<typeof companySettingsSchema>;
export type PlanningSettings = z.infer<typeof planningSettingsSchema>;
export type PayrollSettings = z.infer<typeof payrollSettingsSchema>;
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;
export type BusinessSettings = z.infer<typeof businessSettingsSchema>;
export type SettingsSection = keyof BusinessSettings;

export const DEFAULT_BUSINESS_SETTINGS: BusinessSettings = {
  company: companySettingsSchema.parse({ name: 'Ménage Éclat' }),
  planning: planningSettingsSchema.parse({}),
  pricing: DEFAULT_PRICING_CONFIG,
  payroll: payrollSettingsSchema.parse({}),
  notifications: notificationSettingsSchema.parse({}),
};
